import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DefaultTheme } from 'styled-components';

import { HEADER_SETTINGS_DEFAULTS } from 'src/common/constants';
import { NotificationStatusEnum } from 'src/common/constants';
import { errorHandler } from 'src/common/helpers';
import { DEFAULT_LANGUAGE, DEFAULT_CURRENCY } from 'src/config';
import { ResolutionString } from 'src/lib/charting_library/charting_library';
import { Theme } from 'src/setup/theme';
import { darkColors, lightColors } from 'src/setup/theme/colors';
import {
    IndexConfig,
    ConfigV2,
    Symbol,
    FutureSymbol,
    TokenRateObj,
    TokenRate,
    UserWallet,
    CoinToken,
    NotificationsResponseDTO,
} from 'src/types';
import { CommonConfig } from 'src/types/commonConfig';
import { Token } from 'src/types/configV2';

type WalletTypeNames = {
    [key: number]: string;
};
type HeaderOptionType = {
    favoriteChartTypes: string[];
    activeChartType: number;
    favoriteIntervals: ResolutionString[];
    chartInterval: string;
};

const WALLET_TYPES_NAMES: WalletTypeNames = {
    1: 'SPOT',
    3: 'DERIVATIVES',
    27: 'MARGIN',
};
export interface GlobalState {
    theme: DefaultTheme;
    themeMode: 'light' | 'dark';
    indexConfig: IndexConfig;
    configV2: ConfigV2;
    tokenRates: TokenRateObj;
    loading: boolean;
    error?: string;
    isAssetsHidden: boolean;
    userWallets: UserWallet[];
    transferAbleAmount: string | number;
    coinTokens: CoinToken;
    derivativeSettings: HeaderOptionType;
    spotSettings: HeaderOptionType;
    commonConfig: CommonConfig;
    rtl: boolean;
    activeLanguage: string;
    activeCurrency: string;
    notifications?: NotificationsResponseDTO;
    popupNotifications?: NotificationsResponseDTO;
}

export const initialState: GlobalState = {
    theme: Theme,
    themeMode: 'dark',
    indexConfig: {} as IndexConfig,
    configV2: {} as ConfigV2,
    tokenRates: {} as TokenRateObj,
    loading: false,
    error: undefined,
    isAssetsHidden: false,
    transferAbleAmount: 0,
    coinTokens: {} as CoinToken,
    derivativeSettings: HEADER_SETTINGS_DEFAULTS,
    spotSettings: HEADER_SETTINGS_DEFAULTS,
    rtl: localStorage.getItem('rtl') ? String(localStorage.getItem('rtl')) === 'true' : false,
    activeLanguage: localStorage.getItem('activeLanguage') ?? DEFAULT_LANGUAGE,
    activeCurrency: localStorage.getItem('activeCurrency') ?? DEFAULT_CURRENCY,
    notifications: undefined,
    popupNotifications: undefined,
} as GlobalState;

export const callTypes = {
    list: 'list',
    action: 'action',
};

export const globalSlice = createSlice({
    name: 'globalData',
    initialState,
    reducers: {
        // eslint-disable-next-line no-unused-vars
        startCall: (state, action) => {
            state.error = undefined;
            state.loading = true;
        },
        // eslint-disable-next-line no-unused-vars
        callEnded: (state, action) => {
            state.loading = false;
        },
        catchError: (state, action) => {
            state.loading = false;
            const error = errorHandler(action.payload);
            if (error) {
                state.error = error.title;
            }
        },
        updateThemeData: (state, action) => {
            const { isDark } = action.payload;
            if (isDark !== undefined) {
                let colors = darkColors;
                let themeMode: 'light' | 'dark' = 'dark';
                if (!isDark) {
                    colors = lightColors;
                    themeMode = 'light';
                }
                state.theme = { ...state.theme, colors };
                state.themeMode = themeMode;
            }
            state.theme = { ...state.theme, ...action.payload };
        },
        saveIndexConfig: (state, action) => {
            state.indexConfig = action.payload;
        },
        saveConfigV2: (state, action) => {
            let data = action.payload;
            let symbols: object = {};
            data.symbol.map((item: Symbol) => {
                symbols = { ...symbols, [item.symbolId]: item };
            });
            let futuresSymbol: object = {};
            data.futuresSymbol.map((item: FutureSymbol) => {
                futuresSymbol = { ...futuresSymbol, [item.symbolId]: item };
            });
            let tokens: object = {};
            data.token.map((item: Token) => {
                tokens = { ...tokens, [item.tokenId]: item };
            });
            data['symbol'] = symbols;
            data['futuresSymbol'] = futuresSymbol;
            data['token'] = tokens;
            state.configV2 = data;
        },
        saveTokenRates: (state, action) => {
            let tokenRates = {} as TokenRateObj;
            action.payload.map((item: TokenRate) => {
                tokenRates = { ...tokenRates, [item.token]: item.rates };
            });
            state.tokenRates = tokenRates;
        },
        updateHiddenAsset: (state, action) => {
            state.isAssetsHidden = action.payload;
        },
        walletSubAccountsFetched: (state, action) => {
            const userWallets: UserWallet[] = action.payload;
            state.userWallets = userWallets.map((wallet: UserWallet) => {
                wallet.accountName = WALLET_TYPES_NAMES[wallet.accountType];
                return wallet;
            });
            state.userWallets = action.payload;
        },
        transferAvailableFetched: (state, action) => {
            state.transferAbleAmount = action.payload.amount;
        },
        transferAvailableMarginFetched: (state, action) => {
            state.transferAbleAmount = action.payload.availWithdrawAmount;
        },
        coinTokensFetched: (state, action) => {
            state.coinTokens = action.payload;
        },
        updateFavoriteChartTypes: (
            state,
            action: PayloadAction<{ type: keyof GlobalState; chartType: string }>,
        ) => {
            const { type, chartType } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;
            const favoriteIntervals = chartState.favoriteChartTypes;
            return {
                ...state,
                [type]: {
                    ...chartState,
                    favoriteChartTypes: favoriteIntervals.includes(chartType)
                        ? favoriteIntervals.filter((id) => id !== chartType)
                        : [...favoriteIntervals, chartType],
                },
            };
        },
        updateChartType: (
            state,
            action: PayloadAction<{ type: keyof GlobalState; chartId: number }>,
        ) => {
            const { type, chartId } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;
            return {
                ...state,
                [type]: {
                    ...chartState,
                    activeChartType: chartId,
                },
            };
        },
        updateChartInterval: (
            state,
            action: PayloadAction<{ type: keyof GlobalState; interval: string }>,
        ) => {
            const { type, interval } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;
            return {
                ...state,
                [type]: {
                    ...chartState,
                    chartInterval: interval,
                },
            };
        },
        updateFavoriteIntervals: (
            state,
            action: PayloadAction<{ type: keyof GlobalState; resolutions: string[] }>,
        ) => {
            const { type, resolutions } = action.payload;
            const chartState = (state[type] as HeaderOptionType) || HEADER_SETTINGS_DEFAULTS;

            return {
                ...state,
                [type]: {
                    ...chartState,
                    favoriteIntervals: resolutions,
                },
            };
        },
        saveSettings: (state, action) => {
            state.commonConfig = action.payload;
        },
        toggleRtl: (state, action) => {
            state.rtl = action.payload;
        },
        setActiveCurrency: (state, action) => {
            state.activeCurrency = action.payload;
        },
        setActiveLanguage: (state, action) => {
            if (action.payload) {
                state.activeLanguage = action.payload;
            }
        },
        saveNotifications: (state, action) => {
            state.notifications = action.payload.data;
        },
        savePopupNotifications: (state, action) => {
            state.popupNotifications = action.payload.data;
        },
        markNotificationAsRead: (state, action: PayloadAction<any>) => {
            const id = action.payload.notification_id;
            const current = state.popupNotifications ?? {
                userNotifications: [],
                total: 0,
                unread: 0,
            };

            state.popupNotifications = {
                ...current,
                userNotifications: current.userNotifications.map((notification) =>
                    notification.id === id
                        ? { ...notification, status: NotificationStatusEnum.READ }
                        : notification,
                ),
            };
        },
    },
});
